import { useEffect, useRef, useState } from "react";
import { useCopyToClipboard } from "react-use";
import { CheckIcon, CopyIcon } from "lucide-react";
import { Button } from "./button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { Input } from "./input";
import useToast from "./toast";

interface ShareLinkDialogProps {
  url: string | null;
  loading: boolean;
  error: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ShareLinkDialog = ({ url, loading, error, open, onOpenChange }: ShareLinkDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [, copy] = useCopyToClipboard();
  const { showSuccess } = useToast();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    }
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, [open]);

  const handleCopy = () => {
    if (!url) return;
    copy(url);
    setCopied(true);
    showSuccess("Link copied to clipboard");
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="pt-4 pb-5">
        <DialogHeader>
          <DialogTitle className="text-base">Share link</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 text-sm">
          {loading && <span className="text-muted-foreground">Generating link…</span>}
          {error && <span className="text-destructive">Unable to generate share link.</span>}
          {url && (
            <>
              <div className="flex gap-2 items-center">
                <Input readOnly value={url} className="text-xs font-mono" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopy}
                  aria-label="Copy link"
                  className="shrink-0"
                >
                  {copied ? <CheckIcon className="size-4" /> : <CopyIcon className="size-4" />}
                </Button>
              </div>
              <span className="text-muted-foreground text-xs">Expires in 1 hour.</span>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export { ShareLinkDialog, type ShareLinkDialogProps };
